// useCompanyFormModal.js
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"; // (추가)
import * as yup from "yup";                            // (추가)
import { get, post, put } from "@utils/axios_api";
import { toast } from 'react-toastify';

/* ==== 1) Yup Validation ==== */
const schema = yup.object().shape({
  region: yup
    .string()
    .required("관할구역을 선택하세요."),
  sizeCategory: yup
    .string()
    .required("사원수을 선택하세요."),

  compName: yup
    .string()
    .required("법인명(사업장명)을 입력하세요.")
    .max(20, "최대 20자까지 가능합니다."),
  bizNo: yup
    .string()
    .required("사업자등록번호를 입력하세요.")
    .max(10, "사업자등록번호는 10자리까지 입력 가능합니다.")
    .matches(/^[0-9]+$/, "숫자만 입력 가능합니다."),
  repName: yup
    .string()
    .required("대표자명을 입력하세요.")
    .max(20, "최대 20자까지 가능합니다."),
  phone: yup
    .string()
    .max(11, "전화번호는 최대 11자입니다.")
    .matches(/^[0-9]*$/, "숫자만 입력 가능합니다."),
  addr1: yup
    .string()
    .required("사업장 주소를 검색하세요."),
  addr2: yup
    .string()
    .max(50, "상세주소는 최대 50자까지 가능합니다."),
  bizType: yup
    .string()
    .required("업태를 선택하세요."),
  bizItem: yup
    .string()
    .max(50, "종목은 최대 50자까지 가능합니다."),
  notice: yup
    .string()
    .max(666, "비고는 최대 666자까지 가능합니다."),
  engCompName: yup
    .string()
    .matches(/^[a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ]*$/, "특수문자는 입력할 수 없습니다."),
});

export function useCompanyFormModal({ isOpen, isEdit, boardNum, onClose, onSuccess, isSuperAdmin }) {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      compCode: "",
      region: "",
      bizNo: "",
      compName: "",
      repName: "",
      phone: "",
      addr1: "",
      addr2: "",
      bizType: "",
      bizItem: "",
      notice: "",
      sizeCategory: "",
      sizeCategoryList: [],
      bizTypeList: [],
      regionList: [],
      engCompName: "",
      managerIds: [],   // 복수 담당자 ID 배열
      managerList: [],
      useYn: "Y",
    },
    shouldFocusError: false,
  });

  const { handleSubmit, setValue, watch, reset, formState } = methods;
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // 관할지역 목록
      get("/admin/corp/regionCodes")
        .then((res) => {
          if (res.status !== false && res.result) setValue("regionList", res.result);
        })
        .catch((err) => console.error("관할지역 목록 불러오기 실패:", err));

      // 사내규모 고정 옵션
      setValue("sizeCategoryList", [
        { comdCode: "1", comdName: "50인 미만" },
        { comdCode: "2", comdName: "50인 이상" },
      ]);

      // 업태 목록
      get("/admin/corp/bizTypeCodes")
        .then((res) => {
          if (res.status !== false && res.result) setValue("bizTypeList", res.result);
        })
        .catch((err) => console.error("업태 목록 불러오기 실패:", err));

      // 담당자 후보 목록 — R0(슈퍼관리자)만 조회
      if (isSuperAdmin) {
        get("/admin/corp/managerList")
          .then((res) => {
            if (res.status !== false && res.result) setValue("managerList", res.result);
          })
          .catch((err) => console.error("담당자 목록 불러오기 실패:", err));
      }

      // 편집 모드 => 상세 조회
      if (isEdit && boardNum) {
        setLoadingDetail(true);
        get(`/admin/corp/${boardNum}`)
          .then((data) => {
            const result = data.result;
            reset({
              compCode: result.compCode || "",
              region: result.region || "",
              bizNo: result.bizNo || "",
              compName: result.compName || "",
              repName: result.repName || "",
              phone: result.phone || "",
              addr1: result.addr1 || "",
              addr2: result.addr2 || "",
              bizType: result.bizType || "",
              bizItem: result.bizItem || "",
              notice: result.notice || "",
              sizeCategory: result.sizeCategory || "",
              engCompName: result.engCompName || "",
              managerIds: result.managerIds || [],
              useYn: result.useYn || "Y",
              regionList: watch("regionList") || [],
              sizeCategoryList: watch("sizeCategoryList") || [],
              bizTypeList: watch("bizTypeList") || [],
              managerList: watch("managerList") || [],
            });
          })
          .catch((err) => {
            console.error("상세 데이터 조회 실패:", err);
            setDetailError(err);
          })
          .finally(() => setLoadingDetail(false));
      } else {
        // 등록 모드
        reset({
          compCode: "",
          region: "",
          bizNo: "",
          compName: "",
          repName: "",
          phone: "",
          addr1: "",
          addr2: "",
          bizType: "",
          bizItem: "",
          notice: "",
          sizeCategory: "",
          engCompName: "",
          managerIds: [],
          regionList: watch("regionList") || [],
          sizeCategoryList: watch("sizeCategoryList") || [],
          bizTypeList: watch("bizTypeList") || [],
          managerList: watch("managerList") || [],
        });
      }
    }
  }, [isOpen, isEdit, boardNum, reset, setValue, watch]);

  // [2] 저장 처리
  const onSubmit = async (formData) => {
    // 등록 모드에서 engCompName 필수 체크
    if (!isEdit && !formData.engCompName?.trim()) {
      toast.warn("영문 회사명(계정 ID)을 입력하세요.");
      return;
    }

    // 저장 전 confirm
    if (!window.confirm("저장하시겠습니까?")) {
      return;
    }

    try {
      const url = isEdit
        ? `/admin/corp/${formData.compCode}`
        : "/admin/corp/companyCreate";
      const apiMethod = isEdit ? put : post;
      const response = await apiMethod(url, formData);

      if (onSuccess) onSuccess(response);
      onClose();
    } catch (err) {
      console.error("저장 실패:", err);
    }
  };

  const handleFormSubmit = handleSubmit(onSubmit);

  return {
    ...methods,
    handleFormSubmit,
    loadingDetail,
    detailError,
    formState,
  };
}
